import { Markdown } from '@cadstrom/sigma-engine-shared';
import { $nodeSchema, $view } from '@milkdown/kit/utils';

import {
	getModuleMetadataDetail,
	getModuleSearchMatches,
	getModuleSearchTerm,
} from '#components/cad-renderer/model-reference-search.js';
import {
	createReferenceChipNodeView,
	createReferenceSuggestion,
	getDirectiveReferenceValue,
	isInlineReferenceDirectiveNode,
	isReferenceDirectiveNode,
} from './markdown-editor-reference-plugin.js';
import { BasicRemarkPlugin } from './markdown-plugins-utils.js';
import { ModelModuleMarkdownItem } from './module-reference-markdown-item.js';

import type { ModelWithNetStats } from '@cadstrom/sigma-engine-shared';
import type {
	MarkdownReferenceEditorPlugin,
	MarkdownReferenceSuggestion,
	ReferenceChipViewController,
} from './markdown-editor-reference-plugin.js';
import type { RemarkMarkdownDirectiveDefinition } from './markdown-plugins-utils.js';

const MODULE_REFERENCE_SHORTHAND_PATTERN = /\$([A-Za-z]+(?:[A-Za-z0-9_.-]*[A-Za-z0-9])?)/g;

const MODULE_REFERENCE_SHORTHAND_VALUE_PATTERN = /^[A-Za-z]+(?:[A-Za-z0-9_.-]*[A-Za-z0-9])?$/;

export const MODULE_REFERENCE_NODE_NAME = 'moduleReference';

/**
 * Returns true when the given value can be written using the `$U1` shorthand form.
 */
export function isValidModuleReferenceShorthand(value: string) {
	return MODULE_REFERENCE_SHORTHAND_VALUE_PATTERN.test(value);
}

/**
 * Formats a module reference designator as markdown, preferring the `$` shorthand and falling back to a directive.
 */
export function formatModuleReferenceMarkdown(moduleRef: string) {
	return isValidModuleReferenceShorthand(moduleRef) ?
			`${Markdown.ModuleReferenceMarkdownDirectiveDefinition.editorMarker}${moduleRef}`
		:	`:${Markdown.ModuleReferenceMarkdownDirectiveDefinition.directive}[${escapeDirectiveContent(moduleRef)}]`;
}

function escapeDirectiveContent(value: string) {
	return value.replace(/([\\[\]])/g, '\\$1');
}

/**
 * Remark plugin definition for module reference directives
 */
export const moduleMarkdownItemDefinition: RemarkMarkdownDirectiveDefinition<
	typeof Markdown.ModuleReferenceMarkdownDirectiveDefinition
> = {
	allowedAttributes: Markdown.ModuleReferenceMarkdownDirectiveDefinition.allowedAttributes,
	directive: BasicRemarkPlugin(
		Markdown.ModuleReferenceMarkdownDirectiveDefinition.directive,
		Markdown.NodeTypes.AllDirectives,
		{
			contentPropName: Markdown.ModuleReferenceMarkdownDirectiveDefinition.contentAttributeName,
			shorthandPattern: MODULE_REFERENCE_SHORTHAND_PATTERN,
		},
	),
	item: ModelModuleMarkdownItem,
};

/**
 * Milkdown schema for inline module reference atoms.
 */
export const moduleReferenceSchema = $nodeSchema(MODULE_REFERENCE_NODE_NAME, () => ({
	inline: true,
	group: 'inline',
	atom: true,
	selectable: true,
	draggable: false,
	marks: '',
	attrs: {
		[Markdown.ModuleReferenceMarkdownDirectiveDefinition.contentAttributeName]: { default: '', validate: 'string' },
	},
	parseDOM: [
		{
			tag: `span[data-markdown-reference-kind="${Markdown.ModuleReferenceMarkdownDirectiveDefinition.directive}"]`,
			getAttrs: (dom) => {
				if (!(dom instanceof HTMLElement)) {
					return false;
				}
				return {
					[Markdown.ModuleReferenceMarkdownDirectiveDefinition.contentAttributeName]:
						dom.getAttribute(`data-${Markdown.ModuleReferenceMarkdownDirectiveDefinition.contentAttributeName}`) ?? '',
				};
			},
		},
	],
	toDOM: (node) => {
		const moduleRef = String(node.attrs[Markdown.ModuleReferenceMarkdownDirectiveDefinition.contentAttributeName]);
		return [
			'span',
			{
				'data-markdown-reference-kind': Markdown.ModuleReferenceMarkdownDirectiveDefinition.directive,
				[`data-${Markdown.ModuleReferenceMarkdownDirectiveDefinition.contentAttributeName}`]: moduleRef,
				class: 'inline-flex align-baseline',
			},
			moduleRef,
		];
	},
	leafText: (node) =>
		formatModuleReferenceMarkdown(
			String(node.attrs[Markdown.ModuleReferenceMarkdownDirectiveDefinition.contentAttributeName]),
		),
	parseMarkdown: {
		match: (node) => isReferenceDirectiveNode(node, Markdown.ModuleReferenceMarkdownDirectiveDefinition.directive),
		runner: (state, node, type) => {
			const moduleRef = getDirectiveReferenceValue(
				node,
				Markdown.ModuleReferenceMarkdownDirectiveDefinition.contentAttributeName,
			);
			if (moduleRef === undefined) {
				return;
			}

			if (isInlineReferenceDirectiveNode(node)) {
				state.addNode(type, { [Markdown.ModuleReferenceMarkdownDirectiveDefinition.contentAttributeName]: moduleRef });
				return;
			}

			const paragraph = state.schema.nodes['paragraph'];
			if (paragraph === undefined) {
				state.addNode(type, { [Markdown.ModuleReferenceMarkdownDirectiveDefinition.contentAttributeName]: moduleRef });
				return;
			}

			state
				.openNode(paragraph)
				.addNode(type, { [Markdown.ModuleReferenceMarkdownDirectiveDefinition.contentAttributeName]: moduleRef })
				.closeNode();
		},
	},
	toMarkdown: {
		match: (node) => node.type.name === MODULE_REFERENCE_NODE_NAME,
		runner: (state, node) => {
			const moduleRef = String(node.attrs[Markdown.ModuleReferenceMarkdownDirectiveDefinition.contentAttributeName]);
			if (isValidModuleReferenceShorthand(moduleRef)) {
				state.addNode('text', undefined, formatModuleReferenceMarkdown(moduleRef));
				return;
			}

			state.addNode('textDirective', [{ type: 'text', value: moduleRef }], undefined, {
				name: Markdown.ModuleReferenceMarkdownDirectiveDefinition.directive,
				attributes: {
					[Markdown.ModuleReferenceMarkdownDirectiveDefinition.contentAttributeName]: moduleRef,
				},
			});
		},
	},
}));

/**
 * Creates the ProseMirror node view that mounts React module chips for module reference atoms.
 */
export function createModuleReferenceViewPlugin(controller: ReferenceChipViewController) {
	return $view(moduleReferenceSchema.node, () =>
		createReferenceChipNodeView(
			MODULE_REFERENCE_NODE_NAME,
			Markdown.ModuleReferenceMarkdownDirectiveDefinition.contentAttributeName,
			Markdown.ModuleReferenceMarkdownDirectiveDefinition.directive,
			controller,
		),
	);
}

/**
 * Returns module reference suggestions matching the current shorthand query.
 */
export function getModuleReferenceSuggestions(
	coreModel: ModelWithNetStats,
	query: string,
): MarkdownReferenceSuggestion[] {
	return getModuleSearchMatches(coreModel, getModuleSearchTerm(query), { includeEmpty: true }).map((match) =>
		createReferenceSuggestion(
			Markdown.ModuleReferenceMarkdownDirectiveDefinition.directive,
			match.moduleReferenceDesignator,
			match.moduleReferenceDesignator,
			getModuleReferenceSuggestionDetail(match),
		),
	);
}

/**
 * Shared editor plugin descriptor for module references.
 */
export const moduleReferenceEditorPlugin: MarkdownReferenceEditorPlugin = {
	nodeName: MODULE_REFERENCE_NODE_NAME,
	kind: Markdown.ModuleReferenceMarkdownDirectiveDefinition.directive,
	marker: Markdown.ModuleReferenceMarkdownDirectiveDefinition.editorMarker,
	attrName: Markdown.ModuleReferenceMarkdownDirectiveDefinition.contentAttributeName,
	suggestionQueryCharClass: 'A-Za-z0-9_.-',
	suggestionHint: 'Type to filter modules',
	schema: moduleReferenceSchema,
	createViewPlugin: createModuleReferenceViewPlugin,
	getSuggestions: getModuleReferenceSuggestions,
	renderChip: renderModuleReferenceChip,
};

/**
 * Renders a module reference atom as a React chip in the live editor.
 */
function renderModuleReferenceChip(value: string, attrs: Record<string, string>) {
	return <ModelModuleMarkdownItem {...attrs} moduleRef={value} />;
}

/**
 * Builds secondary suggestion text from command-bar module match details.
 */
function getModuleReferenceSuggestionDetail(match: ReturnType<typeof getModuleSearchMatches>[number]) {
	const detail = [
		match.moduleReferenceDesignator === match.projectModule.name ? undefined : match.projectModule.name,
		getModuleMetadataDetail(match.projectModule),
	]
		.filter((value) => value !== undefined)
		.join(' | ');
	return detail === '' ? undefined : detail;
}
